<template>
  <div class="app-layout" :class="layoutClasses">
    <!-- Header -->
    <AppHeader v-if="showHeader" />

    <!-- Sidebar -->
    <AppSidebar v-if="showSidebar" />

    <!-- Main Content -->
    <main class="main-content" :class="mainContentClasses">
      <div class="container">
        <!-- Loading overlay -->
        <LoadingOverlay v-if="isLoading" />

        <!-- Page content -->
        <slot />
      </div>
    </main>

    <!-- Floating Estimate Cart Badge -->
    <EstimateCartBadge v-if="showEstimateCart" />

    <NotificationContainer />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AppHeader from '@/components/common/AppHeader.vue'
import AppSidebar from '@/components/common/AppSidebar.vue'
import LoadingOverlay from '@/components/ui/LoadingOverlay.vue'
import EstimateCartBadge from '@/components/common/EstimateCartBadge.vue'
import NotificationContainer from '@/components/ui/NotificationContainer.vue'

export default {
  name: 'AppLayout',
  components: {
    AppHeader,
    AppSidebar,
    LoadingOverlay,
    EstimateCartBadge,
    NotificationContainer
  },
  computed: {
    ...mapGetters('app', ['isLoading', 'sidebarCollapsed', 'sidebarHovering']),
    ...mapGetters('auth', ['isAdmin', 'isSuperAdmin', 'isAuthenticated', 'hasRole']),

    layoutType() {
      return this.$route.meta.layout || 'default'
    },

    layoutClasses() {
      return [
        `layout--${this.layoutType}`,
        {
          'layout--loading': this.isLoading,
          'layout--sidebar-collapsed': this.sidebarCollapsed,
          'layout--sidebar-hovering': this.sidebarCollapsed && this.sidebarHovering
        }
      ]
    },

    showHeader() {
      return this.$route.meta.requiresAuth || false
    },

    showSidebar() {
      return this.$route.meta.requiresAuth || false
    },

    showEstimateCart() {
      return (
        this.isAuthenticated &&
        (this.isSuperAdmin || this.isAdmin || this.hasRole('sales')) &&
        this.$route.name !== 'PusherPricer'
      )
    },

    mainContentClasses() {
      return {}
    }
  },
  methods: {
    ...mapActions('app', ['setSidebarMenuItems']),

    initializeSidebarMenuItems() {
      const menuItems = []

      // Dashboard - first, for every role (T11). The sidebar renders in push order.
      if (this.isSuperAdmin || this.isAdmin || this.hasRole('sales')) {
        menuItems.push({
          name: 'dashboard',
          label: 'Dashboard',
          to: '/dashboard',
          icon: ['fas', 'chart-line'],
          order: 1,
          roles: ['super admin', 'admin', 'sales']
        })
      }

      // Quick Spire - for super admin, admin, and sales users (with nested Estimates)
      if (this.isSuperAdmin || this.isAdmin || this.hasRole('sales')) {
        menuItems.push({
          name: 'quick-spire',
          label: 'Grid Pricing',
          to: '/quick-spire',
          icon: ['fas', 'bolt'],
          order: 5,
          roles: ['super admin', 'admin', 'sales'],
          children: [
            {
              name: 'estimates',
              label: 'Estimates',
              to: '/estimates',
              roles: ['super admin', 'admin', 'sales']
            },
            {
              name: 'groups',
              label: 'Groups',
              to: '/groups',
              roles: ['super admin', 'admin', 'sales']
            }
          ]
        })
      }

      // Pusher Pricer - for super admin, admin, and sales users
      if (this.isSuperAdmin || this.isAdmin || this.hasRole('sales')) {
        menuItems.push({
          name: 'pusher-pricer',
          label: 'Pusher Pricer',
          to: '/pusher-pricer',
          icon: ['fas', 'calculator'],
          order: 7,
          roles: ['super admin', 'admin', 'sales']
        })
      }

      // Sell Sheets - for super admin and admin (with nested Templates)
      if (this.isSuperAdmin || this.isAdmin) {
        menuItems.push({
          name: 'sell-sheets',
          label: 'Sell Sheets',
          to: '/sell-sheets',
          icon: ['fas', 'file-invoice'],
          order: 3,
          roles: ['super admin', 'admin'],
          children: [
            {
              name: 'sell-sheet-templates',
              label: 'Sell Sheet Templates',
              to: '/sell-sheet-templates',
              roles: ['super admin', 'admin']
            }
          ]
        })
      }

      if (this.isSuperAdmin || this.isAdmin || this.hasRole('sales')) {
        menuItems.push({
          name: 'catalogue',
          label: 'Parts & Products',
          to: '/catalogue',
          icon: ['fas', 'book'],
          order: 4,
          roles: ['super admin', 'admin', 'sales']
        })
      }

      // Storefront - its own top-level item, directly after Parts & Products.
      // The sidebar renders in push order; the `order` field is not read.
      if (this.isSuperAdmin || this.isAdmin || this.hasRole('sales')) {
        menuItems.push({
          name: 'storefront',
          label: 'Visual Merchandiser',
          to: '/visual-merchandiser',
          icon: ['fas', 'store'],
          order: 4,
          roles: ['super admin', 'admin', 'sales']
        })
      }

      // Plain divider separating main modules from Media / Users
      menuItems.push({ name: 'divider-bottom', divider: true })

      // Media - all roles
      if (this.isSuperAdmin || this.isAdmin || this.hasRole('sales')) {
        menuItems.push({
          name: 'media',
          label: 'Media',
          to: '/media',
          icon: ['fas', 'images'],
          order: 4,
          roles: ['super admin', 'admin', 'sales']
        })
      }

      // Users - for super admin and admin users
      if (this.isSuperAdmin || this.isAdmin) {
        menuItems.push({
          name: 'users',
          label: 'Users',
          to: '/users',
          icon: ['fas', 'users'],
          order: 1,
          roles: ['super admin', 'admin']
        })
      }

      this.setSidebarMenuItems(menuItems)
    }
  },
  watch: {
    // Re-initialize menu items when authentication or admin status changes
    isAuthenticated() {
      this.initializeSidebarMenuItems()
    },
    isAdmin() {
      this.initializeSidebarMenuItems()
    },
    isSuperAdmin() {
      this.initializeSidebarMenuItems()
    }
  },
  mounted() {
    // Initialize sidebar menu items when the layout mounts
    this.initializeSidebarMenuItems()
  }
}
</script>

<style scoped>
.app-layout {
  min-height: 100vh;
  display: grid;
  grid-template-areas:
    "sidebar header"
    "sidebar main";
  grid-template-columns: 280px 1fr;
  grid-template-rows: 60px 1fr;
}

.layout--sidebar-collapsed {
  grid-template-columns: 60px 1fr;
}

.layout--sidebar-collapsed.layout--sidebar-hovering {
  grid-template-columns: 240px 1fr;
}

.main-content {
  grid-area: main;
  overflow-y: auto;
  background-color: #f5f5f5;
}

.container {
  width: 100%;
  height: 100%;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .app-layout {
    grid-template-areas:
      "header"
      "main";
    grid-template-columns: 1fr;
    grid-template-rows: 60px 1fr;
  }
}
</style>
